"use client"

import { useEffect, useState } from "react"
import { WatchPromiseTimeoutError, watchPromise, type WatchEvent } from "@mylaunch/pwa/lib/watch-promise"
import { logPushDiagnostic } from "../lib/log-push-diagnostic"

type PushSubscriptionOptions = { diagnostics?: boolean }
type DiagnosticEvent = WatchEvent | { outcome: "error"; elapsedMs: number; error: unknown }

const asError = (error: unknown) => (error instanceof Error ? error : new Error(String(error)))

export const usePushSubscription = ({ diagnostics = false }: PushSubscriptionOptions = {}) => {
  const [subscription, setSubscription] = useState<PushSubscription | null | undefined>(undefined)
  const [diagnosticError, setDiagnosticError] = useState<Error>()

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      setSubscription(null)
      return
    }
    if (!diagnostics) {
      navigator.serviceWorker.ready
        .then((reg) => reg.pushManager.getSubscription())
        .then((sub) => setSubscription(sub))
        .catch(() => setSubscription(null))
      return
    }

    let mounted = true
    let stage = "service_worker_ready"
    const startedAt = Date.now()
    const report = ({ outcome, elapsedMs, error }: DiagnosticEvent) =>
      logPushDiagnostic({ stage, outcome, elapsedMs, error })
    const probe = navigator.serviceWorker.ready.then((reg) => {
      stage = "push_manager_get_subscription"
      return reg.pushManager.getSubscription()
    })
    void watchPromise(probe, { onEvent: report })
      .then((sub) => mounted && setSubscription(sub))
      .catch((error) => {
        if (!(error instanceof WatchPromiseTimeoutError)) {
          report({ outcome: "error", elapsedMs: Date.now() - startedAt, error })
        }
        if (mounted) setDiagnosticError(asError(error))
      })
    return () => {
      mounted = false
    }
  }, [diagnostics])

  return { subscription, setSubscription, diagnosticError }
}
